import { Component, inject, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { lastValueFrom, map } from 'rxjs';
import {
  CreditCardPaymentSummary,
  Reward,
  ShippingOrder,
  ShippingOrderControllerService,
  ShippingOrderItemDetail
} from 'src/app/admin-api';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { GatewayUtil } from 'src/app/utils/gateway.util';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-shipping-order-refund-modal',
  templateUrl: './shipping-order-refund-modal.component.html',
  styleUrl: './shipping-order-refund-modal.component.scss',
  providers: [ConfirmationService]
})
export class ShippingOrderRefundModalComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private ref = inject(DynamicDialogRef);
  private shippingOrderService = inject(ShippingOrderControllerService);
  private confirmationService = inject(ConfirmationService);

  shippingOrder: ShippingOrder = this.config.data.shippingOrder;
  items: ShippingOrderItemDetail[] = this.config.data.items || [];
  rewards: Reward[] = this.config.data.rewards || [];
  refunds: Reward[] = (this.config.data.rewards || []).filter(
    (r) => r.rewardType === 6
  );
  gatewayPayment: any = this.config.data.gatewayPayment || null;
  payment: CreditCardPaymentSummary = this.config.data.payment || null;

  cols = [
    new TableColumn.Builder()
      .setHeader('Produto')
      .setField('productVariantName')
      .setFilter(false)
      .setSortable(false)
      .build()
  ];
  itemsToRefund: ShippingOrderItemDetail[];

  ngOnInit(): void {
    if (
      !this.shippingOrder ||
      this.shippingOrder.shippingOrderTypeId === 1 ||
      this.shippingOrder.shippingOrderStatus === 11
    )
      this.ref.close();
    if (this.shippingOrder.shippingOrderTypeId === 3) {
      this.itemsToRefund = this.refundableRewards?.map((r) => this.item(r));
      if (!this.itemsToRefund?.length && !this.refunds?.length)
        this.itemsToRefund = [...this.items];
      this.cols.push(
        new TableColumn.Builder()
          .setHeader('Pontos')
          .setField('points')
          .setFilter(false)
          .setSortable(false)
          .setDisplayFunction(
            (item: ShippingOrderItemDetail) =>
              this.reward(item)?.points ||
              this.shippingOrder?.orderPointsTotal / (this.items?.length || 1)
          )
          .build()
      );
    } else {
      this.itemsToRefund = [...this.items];
    }
  }

  async confirmRefund() {
    try {
      LoaderService.showLoader();
      this.shippingOrder = await lastValueFrom(
        this.shippingOrderService
          .refundShippingOrder(this.shippingOrder.shippingOrderId)
          .pipe(map((data) => data.result))
      );
      this.ref.close(this.shippingOrder);
    } catch (error) {
      if (error.status === 409) {
        // Conflito no status da millennium
        this.confirmationService.confirm({
          message: 'O status do pedido não permite estorno do envio.'.concat(
            this.shippingOrder.shippingOrderTypeId === 3 &&
              this.itemsToRefund?.length
              ? ' Deseja estornar os pontos da troca?'
              : ''
          ),
          acceptButtonStyleClass: 'p-button-danger',
          acceptLabel: 'Estorna pontos',
          rejectButtonStyleClass: 'p-button-primary',
          rejectLabel: 'Voltar',
          accept: () => this.ref.close(false),
          reject: () => this.ref.close()
        });
      } else {
        AppDialogService.showErrorDialog(error);
      }
    } finally {
      LoaderService.showLoader(false);
    }
  }

  item(reward: Reward): ShippingOrderItemDetail {
    return this.items.find(
      (item) => item.productVariantId === reward.referenceId
    );
  }

  reward(item: ShippingOrderItemDetail): Reward {
    return this.rewards.find(
      (reward) =>
        reward.rewardType === -1 && reward.referenceId === item.productVariantId
    );
  }

  get refundableRewards() {
    return this.rewards.filter(
      (r) =>
        r.rewardType === -1 &&
        !this.refunds.some((refund) => refund.referenceId === r.rewardId)
    );
  }

  get totalPoints() {
    return this.refundableRewards?.length || this.refunds?.length
      ? this.refundableRewards?.reduce(
          (total, reward) => total + reward.points,
          0
        )
      : this.shippingOrder?.orderPointsTotal;
  }

  get gatewayLink() {
    return environment.production ||
      [10, 12, 13, 17, 19, 21].includes(this.payment.gatewayId)
      ? GatewayUtil.gatewayUrl[this.payment.gatewayId]?.concat(this.payment.tid)
      : null;
  }
}
